/**
 * API Service
 * Extends BaseApiService and handles application API calls
 */

import { FormDataResponse, GetFormDataRequest, SaveFormResponseRequest } from '../types/form';
import type {
  GetConsentDataRequest,
  GetConsentDataResponse,
  SaveConsentResponseRequest,
} from '../types/consent';
import { BaseApiService } from './baseApi';

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

class ApiService extends BaseApiService {
  /**
   * Load form data for a given student.
   * In the cookie-based auth flow, authentication is handled by HttpOnly cookies;
   * the SPA only needs to send the studentId.
   */
  async getFormData(payload: GetFormDataRequest): Promise<FormDataResponse> {
    console.log('getFormData', payload);
    
    // In development mode, return mock data
    if (isDevelopment) {
      console.log('[DEV MODE] Mocking getFormData response');
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            formResponse: {
              itemId: 999,
              formData: {
                studentInfo: {
                  studentId: payload.studentId || 'DEV-12345',
                  studentFirstName: 'John',
                  studentLastName: 'Doe',
                  parentName: 'Jane Doe',
                  studentGrade: '5',
                },
                mealPreferences: {
                  breakfast: true,
                  lunch: true,
                  afternoonSnack: false,
                },
                dietaryRestrictions: ['Vegetarian', 'Gluten Free'],
              },
              texts: {
                'General info text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;"><span style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;background-color&#58;rgb(255, 255, 255);display&#58;inline !important;">The answers for the next month are locked in at the 25th. For extra information click<span>&#160;</span></span><a href="https&#58;//ist.ee/" title="here" style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;background-color&#58;rgb(255, 255, 255);">here</a><span style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;background-color&#58;rgb(255, 255, 255);display&#58;inline !important;">&#160;</span><br></div>',
                'Breakfast text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;">Breakfast is available from 08&#58;30 until 9&#58;30</div>',
                'Lunch text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;">Lunch is available at 11&#58;30 until 12&#58;30</div>',
                'Afternoon snack text': '<div style="font-family&#58;Calibri, Arial, Helvetica, sans-serif;font-size&#58;11pt;color&#58;rgb(50, 49, 48);background-color&#58;transparent;">Afternoon snack is available after 14&#58;00 until closing</div>',
              },
            },
          });
        }, 300);
      });
    }
    
    return this.request<FormDataResponse>('/api/getFormData', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, false);
  }

  /**
   * Submit school meals form
   * Sends SaveFormResponseRequest, including accessToken and refreshToken, to backend.
   */
  async submitSchoolMealForm(payload: SaveFormResponseRequest): Promise<{ success: boolean; message?: string }> {
    // In development mode, mock successful submission
    if (isDevelopment) {
      console.log('[DEV MODE] Mocking submitSchoolMealForm response', payload);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            message: 'Form submitted successfully (mocked in development mode)',
          });
        }, 500);
      });
    }
    
    return this.request<{ success: boolean; message?: string }>('/api/saveFormResponse', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getConsentData(payload: GetConsentDataRequest): Promise<GetConsentDataResponse> {
    if (isDevelopment) {
      console.log('[DEV MODE] Mocking getConsentData', payload);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            studentInfo: {
              studentId: payload.studentId || 'DEV-12345',
              studentFirstName: 'Test',
              studentLastName: 'ShareCloud',
              parentName: 'Rait Altmäe',
              studentGrade: '6',
            },
            definitions: [
              {
                definitionItemId: 101,
                currentResponseItemId: 1001,
                title: 'Photo/Video Permission',
                summary: null,
                detailedTextHtml:
                  '<p>I give permission for my child to be photographed and/or videotaped during school activities and for these images to be used in school publications, website, social media, and promotional materials.</p>',
                consentVersion: '2.0',
                changeDescription:
                  "Added 'social media' to the list of platforms where photos/videos may be used.",
                currentConsentResponse: 'Pending',
                respondedVersion: '',
                isUpdatedSincePriorAnswer: true,
                historicalRows: [
                  {
                    itemId: 9001,
                    consentResponse: 'Yes',
                    respondedVersion: '1.0',
                    consentTextSnapshot:
                      '<p>I give permission for my child to be photographed and/or videotaped during school activities and for these images to be used in school publications and website.</p>',
                    modified: '2025-09-15T12:00:00.000Z',
                    respondedBy: 'Rait Altmäe',
                  },
                ],
              },
              {
                definitionItemId: 102,
                currentResponseItemId: 1002,
                title: 'Local Field Trip Permission',
                summary: null,
                detailedTextHtml:
                  '<p>I give permission for my child to participate in local field trips organized by the school. I understand that proper supervision will be provided and parents will be notified in advance of each trip.</p>',
                consentVersion: '1.0',
                changeDescription: null,
                currentConsentResponse: 'No',
                respondedVersion: '1.0',
                isUpdatedSincePriorAnswer: false,
                historicalRows: [],
              },
            ],
          });
        }, 400);
      });
    }

    return this.request<GetConsentDataResponse>('/api/getConsentData', {
      method: 'POST',
      body: JSON.stringify(payload),
    }, false);
  }

  async saveConsentResponse(
    payload: SaveConsentResponseRequest
  ): Promise<{ message?: string }> {
    if (isDevelopment) {
      console.log('[DEV MODE] Mocking saveConsentResponse', payload);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ message: 'Consent saved (mock)' });
        }, 400);
      });
    }

    return this.request<{ message?: string }>('/api/saveConsentResponse', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

export const apiService = new ApiService();
